import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { articlesApi } from '../api/articlesApi'
import type { Article } from '@/types/entities/article'
import type { ArticleType } from '@/types/enums/article-type'

const DEFAULT_PER_PAGE = 12

/**
 * Store для Articles feature
 * Управляет списком статей и текущей статьёй
 */
export const useArticlesStore = defineStore('articles', () => {
  const articles = ref<Article[]>([])
  const currentArticle = ref<Article | null>(null)
  const loading = ref(false)
  const error = ref<string | null>(null)

  // Current filters
  const currentType = ref<ArticleType | undefined>(undefined)
  const currentTags = ref<string[]>([])

  // Pagination
  const currentPage = ref(1)
  const perPage = ref(DEFAULT_PER_PAGE)
  const total = ref(0)

  const totalPages = computed(() => {
    if (perPage.value <= 0) return 1
    return Math.ceil(total.value / perPage.value)
  })

  const hasNextPage = computed(() => currentPage.value < totalPages.value)

  const loadingMore = ref(false)

  /**
   * Загрузить список статей с фильтрами (первая страница, сброс списка)
   */
  async function loadArticles(type?: ArticleType, tags: string[] = []) {
    loading.value = true
    error.value = null
    currentType.value = type
    currentTags.value = tags
    currentPage.value = 1
    articles.value = []

    try {
      const response = await articlesApi.list(
        type,
        { page: 1, perPage: perPage.value },
        tags
      )
      articles.value = response.articles
      total.value = response.pagination.total || 0
      currentPage.value = response.pagination.page || 1
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load articles'
      console.error('Failed to load articles:', e)
    } finally {
      loading.value = false
    }
  }

  /**
   * Подгрузить следующую страницу (lazy load)
   */
  async function loadMore() {
    if (!hasNextPage.value || loadingMore.value) return

    loadingMore.value = true
    const nextPage = currentPage.value + 1

    try {
      const response = await articlesApi.list(
        currentType.value,
        { page: nextPage, perPage: perPage.value },
        currentTags.value
      )
      articles.value = [...articles.value, ...response.articles]
      total.value = response.pagination.total || total.value
      currentPage.value = nextPage
    } catch (e) {
      console.error('Failed to load more articles:', e)
    } finally {
      loadingMore.value = false
    }
  }

  /**
   * Загрузить статью по slug
   */
  async function loadBySlug(slug: string) {
    loading.value = true
    error.value = null
    currentArticle.value = null

    try {
      currentArticle.value = await articlesApi.getBySlug(slug)
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load article'
      console.error('Failed to load article by slug:', e)
    } finally {
      loading.value = false
    }
  }

  /**
   * Загрузить избранные статьи
   */
  async function loadFeatured() {
    loading.value = true
    error.value = null

    try {
      articles.value = await articlesApi.getFeatured()
    } catch (e) {
      error.value = e instanceof Error ? e.message : 'Failed to load featured articles'
      console.error('Failed to load featured articles:', e)
    } finally {
      loading.value = false
    }
  }

  /**
   * Сбросить состояние
   */
  function reset() {
    articles.value = []
    currentArticle.value = null
    loading.value = false
    error.value = null
    currentType.value = undefined
    currentTags.value = []
    currentPage.value = 1
    total.value = 0
  }

  return {
    // State
    articles,
    currentArticle,
    loading,
    error,
    currentType,
    currentTags,

    // Pagination
    currentPage,
    perPage,
    total,
    totalPages,
    hasNextPage,
    loadingMore,

    // Actions
    loadArticles,
    loadMore,
    loadBySlug,
    loadFeatured,
    reset,
  }
})
